import React, { useEffect, useMemo, useState } from "react";

import { Range, State } from "../../types";
import { drawArrowHead } from "./drawArrowHead";

type Props = Readonly<{
  gutterWidth: number;
  height: number;
  pageRanges: ReadonlyArray<Range>;
  readyPages: Array<boolean>;
  scale: number;
  showReadingOrder: boolean;
  state: State;
  width: number;
}>;

export const Blocks = ({
  gutterWidth,
  height,
  pageRanges,
  readyPages,
  scale,
  showReadingOrder,
  state,
  width,
}: Props) => {
  const canvasStyles = useMemo(
    () => ({
      height: `${height}px`,
      width: `${width}px`,
    }),
    [height, width],
  );
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!canvasRef) {
      return;
    }
    const context = canvasRef.getContext("2d");
    if (!context) {
      return;
    }
    context.clearRect(0, 0, canvasRef.width, canvasRef.height);
    context.lineWidth = 2;
    for (let i = 0; i < state.activeBlockOrder.length; i++) {
      const block = state.activeBlocks[state.activeBlockOrder[i]];
      if (!readyPages[block.page]) {
        continue;
      }
      const blockX = block.minX * scale + gutterWidth;

      // Race condition causes pagination to re-render the blocks 
      // before the pageRanges param had a chance to update, so it may
      // contain the data from the previous page which could have a different size.
      // Once pageRanges does update blocks will render again, fully.
      if (block.page >= pageRanges.length) {
        continue;
      }
      const blockY = block.minY * scale + pageRanges[block.page].start;
      const blockWidth = (block.maxX - block.minX) * scale;
      const blockHeight = (block.maxY - block.minY) * scale;



      let blockIsLinked = false;
      let forwardContinuation = block.continuationId;
      let backwardContinuation = block.continuedFromId;

      while (forwardContinuation) {
        let nextBlock = state.blocks[forwardContinuation];

        if (nextBlock.id === state.selectedBlockId) {
          blockIsLinked = true;
          break;
        }
        forwardContinuation = nextBlock.continuationId;
      }

      while (backwardContinuation) {
        let prevBlock = state.blocks[backwardContinuation];

        if (prevBlock.id === state.selectedBlockId) {
          blockIsLinked = true;
          break;
        }
        backwardContinuation = prevBlock.continuedFromId;
      }

      if (state.selectedBlockId === block.id || blockIsLinked) {

      //if (state.selectedBlockId === block.id || state.selectedBlockId === block.continuationId || state.selectedBlockId === block.continuedFromId) {
        context.fillStyle = "rgba(0, 0, 255, 0.3)";
        context.fillRect(blockX, blockY, blockWidth, blockHeight);
      } else {
        context.strokeStyle = "rgba(0, 0, 200, 0.7)";
        context.strokeRect(blockX, blockY, blockWidth, blockHeight);
      }
      if (showReadingOrder && i < state.activeBlockOrder.length - 1 && !block.continuationId) {
        const startX = ((block.maxX + block.minX) / 2) * scale + gutterWidth;
        const startY = ((block.maxY + block.minY) / 2) * scale + pageRanges[block.page].start;
        const nextBlock = state.activeBlocks[state.activeBlockOrder[i + 1]];
        if (pageRanges[nextBlock.page]){
          const endX = ((nextBlock.maxX + nextBlock.minX) / 2) * scale + gutterWidth;
          const endY =
            ((nextBlock.maxY + nextBlock.minY) / 2) * scale + pageRanges[nextBlock.page].start;
          if (startY > endY) {
            context.fillStyle = "rgba(0, 225, 50, 0.7)";
            context.strokeStyle = "rgba(0, 225, 50, 0.7)";
          } else {
            context.fillStyle = "rgba(255, 0, 0, 0.5)";
            context.strokeStyle = "rgba(255, 0, 0, 0.5)";
          }
          context.beginPath();
          context.moveTo(startX, startY);

          const midX = (startX + endX) / 2;
          const midY = (startY + endY) / 2;
      
          const dx = endX - startX;
          const dy = endY - startY;
          const length = Math.sqrt(dx * dx + dy * dy);

          // Offset is proportional to the length
          const offset = length * 0.08;

          // Perpendicular control point
          const controlX = midX + offset * (-dy / length);
          const controlY = midY + offset * (dx / length);

          const curvedMidX = 0.25 * startX + 0.5 * controlX + 0.25 * endX;
          const curvedMidY = 0.25 * startY + 0.5 * controlY + 0.25 * endY;

          // Old version : straight line
          //context.lineTo(endX, endY);

          // New version : arc'd line (red/green lines bulge in a different direction)
          context.quadraticCurveTo(controlX, controlY, endX, endY);
          context.stroke();

          context.beginPath();
          context.arc(startX, startY, 4, 0, 2 * Math.PI, false);
          context.arc(endX, endY, 4, 0, 2 * Math.PI, false);
          context.fill();
          drawArrowHead(
            context,
            { x: startX, y: startY },
            { x: curvedMidX, y: curvedMidY },
            6,
          );
        }
      }
      // TODO : improve this to render partial line accross page breaks when paginating
      if (block.continuationId !== ""){
        const continuationBlock = state.activeBlocks[block.continuationId];
        if (continuationBlock){

          /*let topLeft  = [block.minX, block.maxY]
          let topRight  = [block.maxX, block.maxY]
          let bottomLeft  = [continuationBlock.minX, continuationBlock.minY]
          let bottomRight  = [continuationBlock.maxX, continuationBlock.minY]

          context.beginPath();
          context.moveTo(topLeft[0] * scale  + gutterWidth, topLeft[1] * scale  + pageRanges[block.page].start); // start point
          context.lineTo(topRight[0] * scale + gutterWidth, topRight[1] * scale + pageRanges[block.page].start);
          context.lineTo(bottomRight[0] * scale + gutterWidth, bottomRight[1] * scale + pageRanges[block.page].start);
          context.lineTo(bottomLeft[0] * scale + gutterWidth, bottomLeft[1] * scale + pageRanges[block.page].start);
          context.lineTo(topLeft[0] * scale + gutterWidth, topLeft[1] * scale + pageRanges[block.page].start);
          context.closePath(); // connects last point to the first

          if (state.selectedBlockId === block.id || blockIsLinked) {
            context.fillStyle = "rgba(0, 0, 255, 0.2)";
          } else {
            context.fillStyle = "rgba(0, 0, 255, 0.1)";
          }
          context.fill();


          context.beginPath();

          if (state.selectedBlockId === block.id || blockIsLinked) {
            context.strokeStyle = "rgba(0, 0, 255, 0.5)";
          } else {
            context.strokeStyle = "rgba(0, 0, 255, 0.4)";
          }*/


          /*context.moveTo(topLeft[0] * scale  + gutterWidth, topLeft[1] * scale  + pageRanges[block.page].start); // start point
          context.lineTo(bottomLeft[0] * scale + gutterWidth, bottomLeft[1] * scale + pageRanges[block.page].start);
          context.setLineDash([5, 2]); // 10px dash, 3px gap
          context.stroke();

          context.beginPath();
          context.lineTo(topRight[0] * scale + gutterWidth, topRight[1] * scale + pageRanges[block.page].start);
          context.lineTo(bottomRight[0] * scale + gutterWidth, bottomRight[1] * scale + pageRanges[block.page].start);
          context.setLineDash([5, 2]); // 10px dash, 3px gap
          context.stroke();
          */

          /*let offset = 15;

          const blockY = ((block.maxY + block.minY) / 2) * scale + pageRanges[block.page].start
          const continuationY = ((continuationBlock.maxY + continuationBlock.minY) / 2) * scale + pageRanges[block.page].start

          let topMidLeft  = [block.minX * scale  + gutterWidth, blockY]
          let topMidRight  = [block.maxX * scale  + gutterWidth, blockY]
          let bottomMidLeft  = [continuationBlock.minX * scale  + gutterWidth, continuationY]
          let bottomMidRight  = [continuationBlock.maxX * scale  + gutterWidth, continuationY]

          let leftMidPoint = [(topMidLeft[0] + bottomMidLeft[0]) / 2, (topMidLeft[1] + bottomMidLeft[1]) / 2] ;
          let leftDirectionVector = [bottomMidLeft[0] - topMidLeft[0], bottomMidLeft[1] - topMidLeft[1]]
          let leftPerpendicularVector = [-leftDirectionVector[1], leftDirectionVector[0]]
          let leftPerpendicularVectorLength = Math.hypot(leftPerpendicularVector[0], leftPerpendicularVector[1]);
          leftPerpendicularVector[0] /= leftPerpendicularVectorLength;
          leftPerpendicularVector[1] /= leftPerpendicularVectorLength;

          let controlLeft = [
            leftMidPoint[0] + leftPerpendicularVector[0] * offset,
            leftMidPoint[1] + leftPerpendicularVector[1] * offset
          ]

          context.moveTo(topMidLeft[0], topMidLeft[1]); // start point
          context.quadraticCurveTo(controlLeft[0], controlLeft[1], bottomMidLeft[0], bottomMidLeft[1]);
          context.setLineDash([5, 2]); // 10px dash, 3px gap
          context.stroke();

          let rightMidPoint = [(topMidRight[0] + bottomMidRight[0]) / 2, (topMidRight[1] + bottomMidRight[1]) / 2] ;
          let rightDirectionVector = [bottomMidRight[0] - topMidRight[0], bottomMidRight[1] - topMidRight[1]]
          let rightPerpendicularVector = [-rightDirectionVector[1], rightDirectionVector[0]]
          let rightPerpendicularVectorLength = Math.hypot(rightPerpendicularVector[0], rightPerpendicularVector[1]);
          rightPerpendicularVector[0] /= rightPerpendicularVectorLength;
          rightPerpendicularVector[1] /= rightPerpendicularVectorLength;

          let controlRight = [
            rightMidPoint[0] - rightPerpendicularVector[0] * offset,
            rightMidPoint[1] - rightPerpendicularVector[1] * offset
          ]

          context.moveTo(topMidRight[0], topMidRight[1]); // start point
          context.quadraticCurveTo(controlRight[0], controlRight[1], bottomMidRight[0], bottomMidRight[1]);
          context.setLineDash([5, 2]); // 10px dash, 3px gap
          context.stroke();
          context.setLineDash([]);*/

          const startX = ((block.maxX + block.minX) / 2) * scale + gutterWidth;
          const startY = ((block.maxY + block.minY) / 2) * scale + pageRanges[block.page].start;
          const endX = ((continuationBlock.maxX + continuationBlock.minX) / 2) * scale + gutterWidth;

          let endY = pageRanges[pageRanges.length-1].end;
          if (continuationBlock.page < pageRanges.length) {
            endY = ((continuationBlock.maxY + continuationBlock.minY) / 2) * scale + pageRanges[continuationBlock.page].start;
          }

          if (showReadingOrder) {
            context.strokeStyle = "rgba(200, 90, 223, 0.9)";
          } else {
            context.strokeStyle = "rgba(200, 90, 223, 0.5)";
          }
          context.setLineDash([7, 2]); // 10px dash, 3px gap
          context.lineWidth = 3.5;
          context.beginPath();
          context.moveTo(startX, startY);
          context.lineTo(endX, endY);
          context.stroke();
          context.setLineDash([]);
          context.lineWidth = 2;

        }
      }
    }
  });
  return (
    <canvas
      className="blocks"
      height={height}
      ref={setCanvasRef}
      style={canvasStyles}
      width={width}
    />
  );
};
Blocks.displayName = "Background";
